import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Un campo password con l'occhio per vederla.
 *
 * Una password si scrive alla cieca, e alla cieca si sbaglia: un carattere in
 * più preso dal riempimento automatico, una maiuscola rimasta attiva, uno
 * spazio incollato in coda. Senza un modo di guardare ciò che si è scritto,
 * l'unica diagnosi possibile è «password errata» - che non dice niente.
 *
 * Parte nascosta, come deve: il pulsante mostra il testo finché lo si tiene
 * acceso, e il campo torna nascosto alla chiusura della pagina.
 */
const PasswordInput = React.forwardRef(({ className, ...props }, ref) => {
  const [visibile, setVisibile] = React.useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visibile ? "text" : "password"}
        className={cn("pr-10", className)}
        {...props}
      />
      <button
        type="button"
        data-testid="toggle-password"
        onClick={() => setVisibile((v) => !v)}
        // Fuori dall'ordine di tabulazione: chi arriva da tastiera vuole
        // passare dalla password al pulsante di accesso, non a un interruttore.
        tabIndex={-1}
        aria-label={visibile ? "Nascondi la password" : "Mostra la password"}
        title={visibile ? "Nascondi la password" : "Mostra la password"}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-ink-subtle transition-colors hover:text-ink"
      >
        {visibile ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
